import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { api, unwrap } from '@/api/client';
import { Skeleton } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field, RootError, Section, Switch, Textarea } from '@/components/ui/form-controls';
import { Input } from '@/components/ui/input';
import { applyProblemToForm } from '@/lib/forms';
import { useTenantMutation, useTenantSettings, useTenantUsers, type TenantSettingsDto } from './api';
import { formToSettings, settingsToForm } from './helpers';
import { settingsSchema, type SettingsForm } from './schemas';

export function SettingsTab({ tenantId }: { tenantId: string }) {
  const settings = useTenantSettings(tenantId);
  if (!settings.data) return <Skeleton className="h-40 w-full" />;
  return <SettingsForm tenantId={tenantId} current={settings.data} />;
}

function SettingsForm({ tenantId, current }: { tenantId: string; current: TenantSettingsDto }) {
  const { t } = useTranslation();
  const users = useTenantUsers(tenantId);
  const form = useForm<SettingsForm>({ resolver: zodResolver(settingsSchema(t)), values: settingsToForm(current) });
  const save = useTenantMutation(
    tenantId,
    async (body: TenantSettingsDto) => unwrap(await api.PUT('/v1/tenants/{id}/settings', { params: { path: { id: tenantId } }, body })),
    t('admin.common.saved'),
  );
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync(formToSettings(v, current));
    } catch (err) {
      applyProblemToForm(form, err, {
        fields: ['quietStart', 'quietEnd', 'quietTimezone', 'notificationEmails', 'webhookUrl', 'fallbackText', 'i18nOverrides', 'reviewerUserIds', 'testRecipients'],
        aliases: {
          'quiethours.start': 'quietStart',
          'quiethours.end': 'quietEnd',
          'quiethours.timezone': 'quietTimezone',
          i18noverrides: 'i18nOverrides',
          fallbacktext: 'fallbackText',
          testrecipients: 'testRecipients',
        },
      });
    }
  });
  const { errors, isSubmitting } = form.formState;
  const reviewerCandidates = (users.data?.items ?? []).filter((u) => u.roles.some((r) => r === 'tenant.reviewer' || r === 'tenant.admin'));

  return (
    <form className="flex flex-col gap-8" onSubmit={(e) => void onSubmit(e)} noValidate>
      <Section title={t('admin.settings.testRecipients')} description={t('admin.settings.testRecipientsHint')}>
        <div className="grid max-w-xl gap-3">
          <Field
            label={t('admin.settings.testRecipientsLabel')}
            htmlFor="s-test-recipients"
            error={errors.testRecipients?.message}
            hint={t('admin.settings.testRecipientsFieldHint')}
          >
            <Textarea
              id="s-test-recipients"
              rows={4}
              className="font-mono text-xs"
              spellCheck={false}
              placeholder={'+41 79 123 45 67\ntester@example.ch'}
              aria-invalid={!!errors.testRecipients}
              {...form.register('testRecipients')}
            />
          </Field>
        </div>
      </Section>

      <Section title={t('admin.settings.quietHours')} description={t('admin.settings.quietHoursHint')}>
        <div className="grid max-w-xl grid-cols-3 gap-3">
          <Field label={t('admin.settings.start')} htmlFor="qh-start" error={errors.quietStart?.message}>
            <Input id="qh-start" type="time" aria-invalid={!!errors.quietStart} {...form.register('quietStart')} />
          </Field>
          <Field label={t('admin.settings.end')} htmlFor="qh-end" error={errors.quietEnd?.message}>
            <Input id="qh-end" type="time" aria-invalid={!!errors.quietEnd} {...form.register('quietEnd')} />
          </Field>
          <Field label={t('admin.tenants.timezone')} htmlFor="qh-tz" error={errors.quietTimezone?.message}>
            <Input id="qh-tz" aria-invalid={!!errors.quietTimezone} {...form.register('quietTimezone')} />
          </Field>
          <Switch className="col-span-3" label={t('admin.settings.weekendsSilent')} {...form.register('weekendsSilent')} />
        </div>
      </Section>

      <Section title={t('admin.settings.notifications')} description={t('admin.settings.notificationsHint')}>
        <div className="grid max-w-xl gap-3">
          <Field label={t('admin.settings.emails')} htmlFor="n-emails" error={errors.notificationEmails?.message}>
            <Input id="n-emails" aria-invalid={!!errors.notificationEmails} {...form.register('notificationEmails')} />
          </Field>
          <Field label={t('admin.settings.webhookUrl')} htmlFor="n-hook" error={errors.webhookUrl?.message}>
            <Input id="n-hook" placeholder="https://" aria-invalid={!!errors.webhookUrl} {...form.register('webhookUrl')} />
          </Field>
          <Switch label={t('admin.settings.digest')} {...form.register('digest')} />
        </div>
      </Section>

      <Section title={t('admin.settings.reviewers')} description={t('admin.settings.reviewersHint')}>
        <Controller
          control={form.control}
          name="reviewerUserIds"
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              {reviewerCandidates.length === 0 && <p className="text-xs text-muted-foreground">{t('admin.settings.noReviewers')}</p>}
              {reviewerCandidates.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={field.value.includes(u.id)}
                    onChange={(e) => field.onChange(e.target.checked ? [...field.value, u.id] : field.value.filter((x) => x !== u.id))}
                  />
                  {u.displayName} <span className="text-xs text-muted-foreground">{u.email}</span>
                </label>
              ))}
            </div>
          )}
        />
      </Section>

      <Section title={t('admin.settings.messages')} description={t('admin.settings.messagesHint')}>
        <div className="grid max-w-2xl gap-3">
          <Field label={t('admin.settings.fallbackText')} htmlFor="s-fallback" error={errors.fallbackText?.message}>
            <Textarea id="s-fallback" rows={2} {...form.register('fallbackText')} />
          </Field>
          <Field label={t('admin.settings.i18nOverrides')} htmlFor="s-i18n" error={errors.i18nOverrides?.message} hint={t('admin.settings.i18nHint')}>
            <Textarea id="s-i18n" rows={8} className="font-mono text-xs" spellCheck={false} aria-invalid={!!errors.i18nOverrides} {...form.register('i18nOverrides')} />
          </Field>
        </div>
      </Section>

      <div className="flex flex-col gap-2">
        <RootError message={errors.root?.message} />
        <div>
          <Button type="submit" disabled={isSubmitting}>
            {t('admin.common.save')}
          </Button>
        </div>
      </div>
    </form>
  );
}
